import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "./api";

/* Messages linked to one order, shown on the order detail page (Aug 2026). */

export function OrderMessagesCard({ orderId }) {
  const [messages, setMessages] = useState(null);
  const [order, setOrder] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get(`/admin/customer-messages/order/${orderId}`).then((r) => setMessages(r.data || [])).catch(() => setMessages([]));
  };
  useEffect(() => {
    load();
    api.get(`/console/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!body.trim() || !order?.email) return;
    setSending(true);
    try {
      await api.post("/admin/customer-messages/reply", {
        customer_email: order.email, body: body.trim(), order_id: orderId,
      });
      setBody("");
      load();
      toast.success("Sent, it's linked to this order.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send."); }
    finally { setSending(false); }
  };

  if (messages === null) return null;

  return (
    <div className="mt-5 bg-white border rounded-md p-4" style={{ borderColor: "#E8DFCB" }} data-testid="order-messages-card">
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold text-[#6E1E3A]">
          <MessageCircle className="w-4 h-4" /> Messages about this order
        </div>
        {order?.email && (
          <Link to="/admin/messages" className="text-[10px] tracking-[0.2em] uppercase font-semibold underline text-[#6E1E3A]" data-testid="order-messages-open-comms">
            Open full thread
          </Link>
        )}
      </div>
      {messages.length === 0 ? (
        <p className="text-sm italic opacity-60" data-testid="order-messages-empty">No messages linked to this order yet.</p>
      ) : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {messages.map((m) => (
            <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`} data-testid={`order-msg-${m.id}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${m.sender === "admin" ? "bg-[#6E1E3A] text-white" : "bg-[#FBF6E9] border border-[#E8DFCB] text-[#3C2A1E]"}`}>
                <div className="text-[8px] tracking-[0.2em] uppercase font-semibold opacity-70 mb-0.5">
                  {m.sender === "admin" ? "You" : (m.customer_name || m.customer_email)}
                  {m.priority === "urgent" && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-red-700 text-white">Urgent</span>}
                  {m.priority === "high" && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-white">Important</span>}
                </div>
                <div className="whitespace-pre-wrap">{m.body}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {order?.email && (
        <div className="mt-3 flex gap-2">
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={`Message ${order.customer_name || order.email} about this order…`}
            data-testid="order-msg-input"
            className="flex-1 h-10 px-3 border rounded text-sm" style={{ borderColor: "#E8DFCB" }}
          />
          <button type="button" onClick={send} disabled={sending || !body.trim()} data-testid="order-msg-send"
            className="h-10 px-4 rounded text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
            style={{ backgroundColor: "#6E1E3A" }}>
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
          </button>
        </div>
      )}
    </div>
  );
}
